import type { ColumnDef } from '@tanstack/react-table'
import type { ITour } from '@/shared/api/types/tour-types'
import type { PaginationConfigInput } from '@/shared/ui/data-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TourDeleteButton } from '@/features/tour/tour-delete-button'
import { TourStatusToggle } from '@/features/tour/tour-status-toggle'
import { formatDateTime } from '@/shared/api/utils/helpers'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { DataTable } from '@/shared/ui/data-table'
import { resolveDashboardLocalizedValue } from '@/shared/utils/dashboard-localized-value'

interface TourListTableProps {
  tours: ITour[]
  loading: boolean
  pagination: PaginationConfigInput
  languageCode: string
  onPageChange: (page: number, pageSize: number) => void
  onEdit: (tour: ITour) => void
}

/**
 * Displays tours with localized names, deletion status controls, and pagination.
 */
export function TourListTable({
  tours,
  loading,
  pagination,
  languageCode,
  onPageChange,
  onEdit,
}: TourListTableProps) {
  const { t } = useTranslation()
  const columns = useMemo<ColumnDef<ITour>[]>(
    () => [
      {
        id: 'tour',
        header: t('tour.name'),
        cell: ({ row }) => {
          const name = resolveDashboardLocalizedValue(row.original.localizedData, languageCode)
          const translation =
            row.original.localizedData.find((item) => item.language === languageCode) ??
            row.original.localizedData[0]
          const description = translation?.description?.trim() ?? ''
          return (
            <div className="flex min-w-[180px] items-center gap-3">
              <Avatar className="h-11 w-11 rounded-md">
                <AvatarImage src={row.original.thumbnail} alt={name} className="object-cover" />
                <AvatarFallback className="rounded-md">
                  {name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-zinc-900">
                  {name || t('tour.noName')}
                </p>
                <p className="truncate text-xs text-zinc-500 line-clamp-2 max-w-[300px]">
                  {description || t('tour.noDescription')}
                </p>
              </div>
            </div>
          )
        },
      },
      {
        id: 'poiCount',
        header: t('tour.poiCount', { defaultValue: 'POIs' }),
        cell: ({ row }) => row.original.poiNumber,
      },
      {
        id: 'duration',
        header: t('tour.duration'),
        cell: ({ row }) => `${row.original.duration}m`,
      },
      {
        id: 'updatedAt',
        header: t('common.updatedAt', { defaultValue: 'Updated at' }),
        cell: ({ row }) => formatDateTime(row.original.updatedAt),
      },
      {
        id: 'status',
        header: () => <div className="text-center">{t('common.status')}</div>,
        cell: ({ row }) => (
          <TourStatusToggle tourId={row.original.id} isActive={row.original.isActive} />
        ),
      },
      {
        id: 'actions',
        size: 64,
        header: () => null,
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <TourDeleteButton tourId={row.original.id} />
          </div>
        ),
      },
    ],
    [t, languageCode],
  )

  return (
    <DataTable
      columns={columns}
      data={tours}
      loading={loading}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: onPageChange,
      }}
      onRowClick={onEdit}
    />
  )
}
